import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { BASE_URL } from '../config/api';
import SEOHead from '../components/SEOHead';
import './EquipmentDetail.css';

// react-snap prerenders with userAgent "ReactSnap", using an old bundled
// Chromium that can crash parsing modern syntax in third-party embed scripts
// (confirmed with Google Maps' embed script — see ContactMap.js). YouTube's
// embed player is the same class of risk and untested against it, so skip it
// during prerender too; real visitors still get the live embed on hydration.
const isPrerender =
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BASE_URL}${path.startsWith('/') ? '' : '/media/'}${path}`;
};

const EquipmentDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [mediaHeight, setMediaHeight] = useState(0);
  const mediaRef = useRef(null);

  useEffect(() => {
    const fetchItem = async () => {
      try {
        const res = await api.get(`/content/equipment/${slug}/`);
        setItem(res.data);
      } catch (e) {
        setError(i18n.language === 'ka' ? 'აღჭურვილობის ჩატვირთვა ვერ მოხერხდა' : 'Failed to load equipment');
      } finally {
        setLoading(false);
      }
    };
    fetchItem();
  }, [slug, i18n.language]);

  const localized = useMemo(() => {
    if (!item) return null;
    const isKa = i18n.language === 'ka';
    const title = (isKa ? (item.title_ka || item.title_en) : (item.title_en || item.title_ka)) || '';
    const longDesc = (isKa ? (item.long_description_ka || item.long_description_en) : (item.long_description_en || item.long_description_ka)) || '';
    const shortDesc = (isKa ? (item.short_description_ka || item.short_description_en) : (item.short_description_en || item.short_description_ka)) || '';
    const seoTitle = (isKa ? (item.meta_title_ka || item.meta_title_en) : (item.meta_title_en || item.meta_title_ka)) || '';
    const seoDescription = (isKa ? (item.meta_description_ka || item.meta_description_en) : (item.meta_description_en || item.meta_description_ka)) || '';

    const photos = [];
    const mainPhotoUrl = getImageUrl(item.main_photo);
    const mainPhotoRaw = (item.main_photo || '').toString().trim();
    const mainPhotoName = mainPhotoRaw.split('/').pop();
    (item.secondary_photos || []).forEach((p) => {
      const url = getImageUrl(p.photo);
      if (!url) return;
      const raw = (p.photo || '').toString().trim();
      const name = raw.split('/').pop();
      // Never show the card/main photo inside details media.
      if ((mainPhotoUrl && url === mainPhotoUrl) || (mainPhotoRaw && raw === mainPhotoRaw) || (mainPhotoName && name === mainPhotoName)) {
        return;
      }
      photos.push({ url });
    });

    return { title, longDesc, shortDesc, seoTitle, seoDescription, photos };
  }, [item, i18n.language]);

  const photos = localized?.photos || [];
  const activePhoto = photos[activeIndex];
  const descriptionLabel = i18n.language === 'ka' ? 'აღწერა' : 'Description';
  const videoLabel = i18n.language === 'ka' ? 'ვიდეო' : 'Video';

  useEffect(() => {
    if (!photos.length) {
      setActiveIndex(0);
      return;
    }
    if (activeIndex >= photos.length) {
      setActiveIndex(0);
    }
  }, [photos.length, activeIndex]);

  useEffect(() => {
    if (photos.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % photos.length);
    }, 4500);
    return () => window.clearInterval(timer);
  }, [photos.length]);

  useEffect(() => {
    const mediaEl = mediaRef.current;
    if (!mediaEl) return undefined;

    const updateHeight = () => {
      const nextHeight = Math.round(mediaEl.getBoundingClientRect().height || 0);
      setMediaHeight((prev) => (prev !== nextHeight ? nextHeight : prev));
    };

    updateHeight();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => updateHeight());
      observer.observe(mediaEl);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [photos.length, item?.youtube_embed_url, localized?.longDesc]);

  const next = () => setActiveIndex((i) => (photos.length ? (i + 1) % photos.length : 0));
  const prev = () => setActiveIndex((i) => (photos.length ? (i - 1 + photos.length) % photos.length : 0));

  if (loading) {
    return <div className="equipment-detail-loading">{i18n.language === 'ka' ? 'იტვირთება...' : 'Loading...'}</div>;
  }

  if (error || !localized) {
    return (
      <div className="equipment-detail-error">
        <div>{error || (i18n.language === 'ka' ? 'ვერ მოიძებნა' : 'Not found')}</div>
        <button type="button" className="equipment-detail-back" onClick={() => navigate('/about/equipment')}>
          ← {i18n.language === 'ka' ? 'უკან' : 'Back'}
        </button>
      </div>
    );
  }

  return (
    <div className="equipment-detail-page">
      <SEOHead
        title={localized.seoTitle || `${localized.title} | GFM Studio Gear`}
        description={localized.seoDescription || (localized.shortDesc || localized.longDesc).slice(0, 155)}
        path={`/about/equipment/${slug}`}
        image={item.og_image || item.main_photo || undefined}
        noindex={item.robots_index === false}
      />
      <div className="equipment-detail-topbar">
        <button type="button" className="equipment-detail-back" onClick={() => navigate('/about/equipment')}>
          ← {i18n.language === 'ka' ? 'უკან' : 'Back'}
        </button>
      </div>

      <div
        className={`equipment-detail-layout ${photos.length === 0 ? 'equipment-detail-layout-no-media' : ''}`}
        style={photos.length > 0 && mediaHeight ? { '--equipment-media-height': `${mediaHeight}px` } : undefined}
      >
        {photos.length > 0 ? (
          <div className="equipment-detail-media" ref={mediaRef}>
            <div className="equipment-detail-slider">
              <div className="equipment-detail-frame">
                <img key={activeIndex} src={activePhoto.url} alt={localized.title} className="equipment-detail-image" />
                <div className="equipment-detail-overlay" />
              </div>
              {photos.length > 1 ? (
                <>
                  <button type="button" className="equipment-detail-nav prev" onClick={prev} aria-label="Previous">
                    ‹
                  </button>
                  <button type="button" className="equipment-detail-nav next" onClick={next} aria-label="Next">
                    ›
                  </button>
                  <div className="equipment-detail-dots">
                    {photos.map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`equipment-detail-dot ${idx === activeIndex ? 'active' : ''}`}
                        onClick={() => setActiveIndex(idx)}
                        aria-label={`Go to image ${idx + 1}`}
                      />
                    ))}
                  </div>
                  <div className="equipment-detail-thumbs" role="tablist" aria-label="Equipment images">
                    {photos.map((photo, idx) => (
                      <button
                        key={`thumb-${idx}`}
                        type="button"
                        role="tab"
                        className={`equipment-detail-thumb ${idx === activeIndex ? 'active' : ''}`}
                        onClick={() => setActiveIndex(idx)}
                        aria-label={`Show image ${idx + 1}`}
                        aria-selected={idx === activeIndex}
                      >
                        <img src={photo.url} alt={`${localized.title} ${idx + 1}`} loading="lazy" />
                        <span className="equipment-detail-thumb-overlay" />
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="equipment-detail-content">
          <h1 className="equipment-detail-title">{localized.title}</h1>
          <h2 className="equipment-detail-section-title">{descriptionLabel}</h2>
          {localized.longDesc ? (
            <div className="equipment-detail-long">
              {String(localized.longDesc)
                .split('\n')
                .map((p, idx) => (p.trim() ? <p key={idx}>{p}</p> : null))}
            </div>
          ) : null}
          {item.youtube_embed_url && !isPrerender ? (
            <div className="equipment-detail-video-card">
              <h2 className="equipment-detail-section-title">{videoLabel}</h2>
              <div className="equipment-detail-video-frame">
                <iframe
                  src={item.youtube_embed_url}
                  title={`${localized.title} video`}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EquipmentDetail;

